import { useEffect, useRef, useState, useCallback } from 'react';

// Tech/Design terms for the split-flap easter egg
// All terms are 9 characters to match "NK.DESIGN"
const TECH_TERMS = [
  'FRONT.END',
  'BACK.END.',
  'FULL.STCK',
  'REACT.APP',
  'NEXT.JS.X',
  'VUE.THREE',
  'TYPE.SAFE',
  'UX.DESIGN',
  'UI.MASTER',
  'FIGMA.PRO',
  'WEB.CRAFT',
  'PIXEL.ART',
  'CODE.LOVE',
  'DEV.TOOLS',
  'API.FIRST',
  'NODE.FAST',
  'CSS.MAGIC',
  'JS.EXPERT',
  'GIT.READY',
  'CLEAN.COD',
  'SOLID.DEV',
  'AGILE.NOW',
  'TAILW.CSS',
  'SVELTE.JS',
];

// Short terms (letters only, no punctuation) for 4-5 char targets like NIKO, KNEZ
const SHORT_TERMS = [
  'CODE', 'REACT', 'VUEJS', 'NEXTS', 'TYPED',
  'HTMLS', 'CSSWZ', 'JSDEV', 'NODEX', 'GITPRO',
  'WEBUI', 'APPUX', 'DEVOP', 'CLOUD', 'APIUX',
  'PIXEL', 'FIGMA', 'UIKIT', 'SWIFT', 'RUSTC',
  'GOLAN', 'PYTHN', 'KUBEX', 'DOCKR', 'LINUX',
  'VIMUX', 'ZSHRC', 'BASHX', 'SQLDB', 'REDIS',
];

interface UseTextScrambleOptions {
  text: string;
  duration?: number;
  delay?: number;
  flipSpeed?: number; // ms per term flip
  endless?: boolean; // Keep cycling forever until stopped
  reverseReveal?: boolean; // Reveal from right to left instead of left to right
}

export function useTextScramble({
  text,
  duration = 1200,
  delay = 0,
  flipSpeed = 80,
  endless = false,
  reverseReveal = false,
}: UseTextScrambleOptions) {
  const [displayText, setDisplayText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const frameRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const endlessRef = useRef(endless);
  const wasEndlessRef = useRef(endless);

  // Update ref when endless prop changes
  // Reset startTime when transitioning from endless to normal mode
  useEffect(() => {
    if (wasEndlessRef.current && !endless) {
      // Transitioning from endless to normal - reset for fresh animation
      startTimeRef.current = 0;
      setIsComplete(false);
    }
    endlessRef.current = endless;
    wasEndlessRef.current = endless;
  }, [endless]);

  const scramble = useCallback(() => {
    const targetText = text.toUpperCase();
    const targetLength = targetText.length;

    // Use SHORT_TERMS for short text (letters only), TECH_TERMS for longer text
    const sourceTerms = targetLength <= 5 ? SHORT_TERMS : TECH_TERMS;

    // Pad/slice terms to match target length (use 'X' padding for short terms, '.' for long)
    const paddedTerms = sourceTerms.map((term) => {
      if (term.length < targetLength) {
        return term.padEnd(targetLength, targetLength <= 5 ? 'X' : '.');
      }
      return term.slice(0, targetLength);
    });

    let lastFlipTime = 0;
    let currentTermIndex = Math.floor(Math.random() * paddedTerms.length);
    const lockPositions: boolean[] = new Array(targetLength).fill(false);

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
        lastFlipTime = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const timeSinceLastFlip = timestamp - lastFlipTime;

      // ENDLESS MODE: Just keep cycling through terms
      if (endlessRef.current) {
        if (timeSinceLastFlip >= flipSpeed) {
          lastFlipTime = timestamp;
          currentTermIndex = (currentTermIndex + 1) % paddedTerms.length;
          const currentTerm = paddedTerms[currentTermIndex] ?? targetText;
          setDisplayText(currentTerm);
        }
        frameRef.current = requestAnimationFrame(animate);
        return;
      }

      // NORMAL MODE: Progress towards target
      const progress = Math.min(elapsed / duration, 1);

      // Flip to next term at regular intervals (faster flips = more dramatic)
      if (timeSinceLastFlip >= flipSpeed && progress < 0.65) {
        lastFlipTime = timestamp;
        currentTermIndex = (currentTermIndex + 1) % paddedTerms.length;
      }

      // Progressively lock in characters (left-to-right or right-to-left based on reverseReveal)
      const charsToLock = Math.floor(progress * 1.4 * targetLength);

      // Build display text
      const currentTerm = paddedTerms[currentTermIndex] ?? targetText;
      const result = targetText.split('').map((targetChar, i) => {
        // Calculate position for locking (reversed if reverseReveal)
        const lockIndex = reverseReveal ? targetLength - 1 - i : i;
        const progressIndex = reverseReveal ? targetLength - 1 - i : i;

        // Lock in final characters progressively
        if (lockIndex < charsToLock || progress >= 0.65 + (progressIndex / targetLength) * 0.35) {
          lockPositions[i] = true;
          return targetChar;
        }
        // Show flipping term character
        return currentTerm[i] ?? targetChar;
      });

      setDisplayText(result.join(''));

      // Check if complete
      const allLocked = lockPositions.every((locked) => locked);
      if (allLocked || progress >= 1) {
        setDisplayText(targetText);
        setIsComplete(true);
      } else {
        frameRef.current = requestAnimationFrame(animate);
      }
    };

    frameRef.current = requestAnimationFrame(animate);
  }, [text, duration, flipSpeed, reverseReveal]);

  useEffect(() => {
    // Initialize with a random tech term (use SHORT_TERMS for short text)
    const targetLength = text.length;
    const sourceTerms = targetLength <= 5 ? SHORT_TERMS : TECH_TERMS;
    const randomTerm = sourceTerms[Math.floor(Math.random() * sourceTerms.length)] ?? text;
    const paddedTerm =
      randomTerm.length < targetLength
        ? randomTerm.padEnd(targetLength, targetLength <= 5 ? 'X' : '.')
        : randomTerm.slice(0, targetLength);
    setDisplayText(paddedTerm);

    const timer = setTimeout(() => {
      scramble();
    }, delay);

    return () => {
      clearTimeout(timer);
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [delay, scramble, text]);

  const reset = useCallback(() => {
    setDisplayText('');
    setIsComplete(false);
    startTimeRef.current = 0;
  }, []);

  return { displayText, isComplete, reset };
}
